import React, { useCallback, useMemo } from 'react';
import { WorkflowNode as WorkflowNodeType } from '../../types/workflow';

interface WorkflowNodeProps {
  node: WorkflowNodeType;
  isSelected: boolean;
  isConnecting: boolean;
  onSelect: (nodeId: string) => void;
  onStartConnection: (nodeId: string, outputId: string) => void;
  onEndConnection: (nodeId: string, inputId: string) => void;
  onUpdateNode: (nodeId: string, updates: Partial<WorkflowNodeType>) => void;
  onDeleteNode: (nodeId: string) => void;
  onBeginDrag?: (nodeId: string, e: React.MouseEvent) => void;
  onHoverInput?: (nodeId: string, inputId: string, rect: DOMRect) => void;
  onLeaveInput?: () => void;
}

export const WorkflowNode: React.FC<WorkflowNodeProps> = ({
  node,
  isSelected,
  isConnecting,
  onSelect,
  onStartConnection,
  onEndConnection,
  onUpdateNode,
  onDeleteNode,
  onBeginDrag,
  onHoverInput,
  onLeaveInput,
}) => {
  const containerStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: node.position.x,
    top: node.position.y,
    width: 192,
  }), [node.position.x, node.position.y]);

  const handleMouseDownHeader = useCallback((e: React.MouseEvent) => {
    onBeginDrag?.(node.id, e);
  }, [node.id, onBeginDrag]);

  const statusColor = useMemo(() => {
    switch (node.status) {
      case 'running':
        return 'bg-yellow-100 text-yellow-800';
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, [node.status]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(node.id);
    if (isConnecting) {
      const inputId = node.inputs && node.inputs.length > 0 ? node.inputs[0].id : 'in';
      onEndConnection(node.id, inputId);
    } else {
      const outputId = node.outputs && node.outputs.length > 0 ? node.outputs[0].id : 'out';
      onStartConnection(node.id, outputId);
    }
  }, [node.id, node.inputs, node.outputs, isConnecting, onSelect, onStartConnection, onEndConnection]);

  return (
    <div
      className={`select-none shadow-sm border ${isSelected ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'} rounded-xl bg-white`}
      style={containerStyle}
      onClick={handleContainerClick}
    >
      <div
        className="px-4 py-3 border-b border-gray-100 flex items-center justify-between rounded-t-xl bg-white cursor-move"
        onMouseDown={handleMouseDownHeader}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-medium text-gray-900 truncate">{node.name}</div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor}`}>{node.status ?? 'idle'}</span>
      </div>

      <div className="p-3 space-y-2">
        {/* Condition editor (simple) */}
        {node.type === 'condition' && (
          <div className="mb-2">
            <label className="block text-xs text-gray-500 mb-1">Expression</label>
            <input
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded"
              value={String(node.data?.expression ?? '')}
              onChange={(e) => onUpdateNode(node.id, { data: { ...node.data, expression: e.target.value } })}
              placeholder="e.g., payload.flag === true"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        {/* Inputs */}
        {node.inputs?.length ? (
          <div className="space-y-2">
            {node.inputs.map((input) => (
              <div key={input.id} className="flex items-center justify-between text-xs text-gray-600">
                <span>{input.name}</span>
                <button
                  className="w-3 h-3 rounded-full bg-gray-300 hover:bg-blue-400"
                  title={`Connect to ${input.name}`}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    onHoverInput?.(node.id, input.id, rect);
                  }}
                  onMouseLeave={(e) => { e.stopPropagation(); onLeaveInput?.(); }}
                  onClick={(e) => { e.stopPropagation(); onEndConnection(node.id, input.id); }}
                />
              </div>
            ))}
          </div>
        ) : null}

        {/* Outputs */}
        {node.outputs?.length ? (
          <div className="space-y-2">
            {node.outputs.map((output) => (
              <div key={output.id} className="flex items-center justify-between text-xs text-gray-600">
                <span>{output.name}</span>
                <button
                  className={`w-3 h-3 rounded-full ${isConnecting ? 'bg-blue-500' : 'bg-gray-300'} hover:bg-blue-400`}
                  title={`Start connection from ${output.name}`}
                  onClick={(e) => { e.stopPropagation(); onStartConnection(node.id, output.id); }}
                />
              </div>
            ))}
          </div>
        ) : null}

        <div className="pt-2 flex items-center justify-end space-x-2">
          <button
            className="text-xs text-gray-500 hover:text-gray-700"
            onClick={(e) => { e.stopPropagation(); onUpdateNode(node.id, { name: node.name }); }}
          >
            Edit
          </button>
          <button
            className="text-xs text-red-500 hover:text-red-700"
            onClick={(e) => { e.stopPropagation(); onDeleteNode(node.id); }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowNode;

